import { NumberResult } from "./Sidebar";
import { useEffect, useLayoutEffect, useState } from "preact/hooks";
import { api, ApiError, getSession, isPrimary, type Device } from "../api";
import * as S from "../state";
import { listTime, phoneOf } from "../util";
import { Avatar, AvatarPicker } from "./Avatar";
import { Icon } from "./Icon";
import type { Msg } from "../db";
import { MessageResult } from "./Dialogs";
import { drawer, engine, infoOpen, toast } from "./Main";
import { ChatRow, sortChats } from "./Sidebar";
import { LinkScanner } from "./Link";
import { ExportChat } from "./Export";
import { ReportButton } from "./Report";

export function Drawer({ title, children }: { title: string; children: preact.ComponentChildren }) {
  useLayoutEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && (drawer.value = "none");
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <div class="drawer" role="dialog" aria-label={title}>
      <header class="drawer-head">
        <button class="icon-btn" onClick={() => (drawer.value = "none")} aria-label="Back">
          <Icon name="back" />
        </button>
        <h2>{title}</h2>
      </header>
      <div class="drawer-body">{children}</div>
    </div>
  );
}

// ---- new chat / contacts ----------------------------------------------------

export function NewChat() {
  const [filter, setFilter] = useState("");
  const [adding, setAdding] = useState(false);
  const f = filter.trim().toLowerCase();
  const list = S.contacts.value
    .filter((c) => !f || c.name.toLowerCase().includes(f) || c.jid.includes(f.replace(/\D/g, "") || "\u0000"))
    .sort((a, b) => a.name.localeCompare(b.name));

  function open(jid: string) {
    drawer.value = "none";
    void engine.value?.openChat(jid);
  }

  return (
    <Drawer title="New chat">
      <div class="search">
        <Icon name="search" size={20} />
        <input autoFocus value={filter} onInput={(e) => setFilter(e.currentTarget.value)} placeholder="Search name or number" aria-label="Search contacts" />
      </div>
      <button class="list-action" onClick={() => (drawer.value = "newGroup")}>
        <span class="list-action-icon">
          <Icon name="group" />
        </span>
        New group
      </button>
      {isPrimary() && <SyncContacts />}
      <button class="list-action" onClick={() => (drawer.value = "newBroadcast")}>
        <span class="list-action-icon">
          <Icon name="send" />
        </span>
        New broadcast
      </button>
      {adding ? (
        <AddContact onDone={(jid) => (setAdding(false), jid && open(jid))} />
      ) : (
        <button class="list-action" onClick={() => setAdding(true)}>
          <span class="list-action-icon">
            <Icon name="personAdd" />
          </span>
          New contact
        </button>
      )}
      {!f && S.frequent.value.length > 0 && (
        <>
          <h3 class="section">Frequently contacted</h3>
          <ul class="contact-list frequent-list">
            {S.frequent.value.slice(0, 5).map((j) => (
              <li key={j} tabIndex={0} onClick={() => open(j)} onKeyDown={(e) => e.key === "Enter" && open(j)}>
                <Avatar url={S.isGroup(j) ? S.groups.value.get(j)?.avatar_url : S.profiles.value.get(j)?.avatar_url} jid={j} name={S.displayName(j)} />
                <div>
                  <div class="chat-name">{S.displayName(j)}</div>
                  <div class="muted small ellipsis">{S.isGroup(j) ? "Group" : phoneOf(j)}</div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      <h3 class="section">Contacts on xmm</h3>
      <ul class="contact-list">
        <NumberResult query={filter} onOpen={() => (drawer.value = "none")} />
        {list.map((c) => {
          const p = S.profiles.value.get(c.jid);
          return (
            <li key={c.jid} tabIndex={0} onClick={() => open(c.jid)} onKeyDown={(e) => e.key === "Enter" && open(c.jid)}>
              <Avatar url={p?.avatar_url} jid={c.jid} name={c.name} />
              <div>
                <div class="chat-name">{c.name || phoneOf(c.jid)}</div>
                <div class="muted small ellipsis">{p?.about ?? phoneOf(c.jid)}</div>
              </div>
            </li>
          );
        })}
        {!list.length && <li class="empty">{f ? "No contacts found." : "No contacts yet. Add one with their phone number."}</li>}
      </ul>
    </Drawer>
  );
}

/** Phone app: sync the address book (private: see contacts.ts). */
function SyncContacts() {
  const [busy, setBusy] = useState(false);
  async function sync() {
    setBusy(true);
    try {
      const r = await engine.value!.syncContacts();
      toast(`${r.found.length} of your contacts use xmm${r.added ? ` · ${r.added} added` : ""}${r.removed ? ` · ${r.removed} removed` : ""}`);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <button class="list-action" disabled={busy} onClick={() => void sync()} title="Your address book never leaves the phone: only short, anonymous hash prefixes are used to find who's on xmm">
      <span class="list-action-icon">
        <Icon name="refresh" />
      </span>
      {busy ? "Syncing your contacts…" : "Sync contacts"}
    </button>
  );
}

function AddContact({ onDone }: { onDone: (jid?: string) => void }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("+");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function save(e: Event) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.addContact(phone, name);
      toast(`${r.name} added to contacts`);
      onDone(r.user.jid);
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === "not_on_xmm"
          ? "This number isn't on xmm yet."
          : err instanceof ApiError && err.status === 404 && phone.startsWith("@")
            ? "No one has this username."
            : err instanceof Error
              ? err.message
              : String(err),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form class="add-contact" onSubmit={save}>
      <input autoFocus value={name} onInput={(e) => setName(e.currentTarget.value)} placeholder="Name" aria-label="Contact name" maxLength={60} />
      <input value={phone} onInput={(e) => setPhone(e.currentTarget.value)} placeholder="+91 98765 43210 or @username" aria-label="Phone number" />
      <small class="muted">A phone number, or someone's @username.</small>
      {error && <p class="error">{error}</p>}
      <div class="row-buttons">
        <button type="button" class="link" onClick={() => onDone()}>
          Cancel
        </button>
        <button class="primary" disabled={busy || (phone.startsWith("@") ? phone.length < 4 : phone.replace(/\D/g, "").length < 8)}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}

// ---- own profile -------------------------------------------------------------

export function ProfilePanel() {
  const me = getSession()!.user;
  const [avatar, setAvatar] = useState(me.avatar_url);
  return (
    <Drawer title="Profile">
      <div class="profile-photo">
        <AvatarPicker url={avatar} jid={me.jid} name={me.name} onChange={setAvatar} />
      </div>
      <EditableField label="Your name" value={me.name} max={25} hint="This is not your username or PIN. This name will be visible to your xmm contacts." save={(name) => api.updateMe({ name })} />
      <EditableField label="About" value={me.about} max={139} save={(about) => api.updateMe({ about })} />
      <EditableField
        label="Username"
        value={me.username ? "@" + me.username : ""}
        max={31}
        placeholder="Not set"
        format={(u) => (u ? "@" + u.replace(/^@/, "").toLowerCase() : "")}
        hint="People can find and message you by @username without your phone number. Letters, digits, dots and underscores."
        save={(u) =>
          api.updateMe({ username: u.replace(/^@/, "") }).catch((e) => {
            throw new Error(e instanceof ApiError && e.code === "username_taken" ? "That username is taken." : (e as Error).message);
          })
        }
      />
      <div class="field">
        <label>Phone</label>
        <div class="field-value">{me.phone}</div>
      </div>
    </Drawer>
  );
}

function EditableField(props: { label: string; value: string; max: number; hint?: string; placeholder?: string; format?: (v: string) => string; save: (v: string) => Promise<unknown> }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(props.value);
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(props.value);

  async function commit(e: Event) {
    e.preventDefault();
    setBusy(true);
    try {
      await props.save(value.trim());
      setShown(props.format ? props.format(value.trim()) : value.trim());
      setEditing(false);
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form class="field" onSubmit={commit}>
      <label>{props.label}</label>
      {editing ? (
        // (Keyed: otherwise Preact reuses the Edit button's node as the Save
        // button during the click, and the form submits straight away.)
        <div class="field-edit" key="edit">
          <input autoFocus value={value} maxLength={props.max} onInput={(e) => setValue(e.currentTarget.value)} aria-label={props.label} />
          <span class="muted small">{props.max - value.length}</span>
          <button type="submit" class="icon-btn" disabled={busy} aria-label="Save">
            <Icon name="check" />
          </button>
        </div>
      ) : (
        <div class="field-value" key="view">
          <span class={shown ? "" : "muted"}>{shown || props.placeholder}</span>
          <button type="button" class="icon-btn" onClick={() => (setValue(shown), setEditing(true))} aria-label={`Edit ${props.label}`}>
            <Icon name="edit" size={20} />
          </button>
        </div>
      )}
      {props.hint && <p class="muted small">{props.hint}</p>}
    </form>
  );
}

// ---- linked devices ------------------------------------------------------------

export function Devices() {
  const [list, setList] = useState<Device[] | null>(null);
  const [error, setError] = useState("");
  const [scanning, setScanning] = useState(false);

  const load = () =>
    api
      .devices()
      .then((r) => setList(r.devices))
      .catch((e) => setError(String(e.message ?? e)));
  useEffect(() => void load(), [S.devicesChanged.value]);

  async function remove(d: Device) {
    try {
      await api.removeDevice(d.id);
      toast(`Logged out ${d.device_name || "device"}`);
      void load();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Drawer title="Linked devices">
      {isPrimary() ? (
        <>
          <button class="list-action" onClick={() => setScanning(true)}>
            <span class="list-action-icon">
              <Icon name="plus" />
            </span>
            Link a device
          </button>
          {scanning && <LinkScanner onClose={() => (setScanning(false), void load())} />}
          <p class="muted pad">
            Use xmm on your computer: open it in a browser and scan its QR code (or enter its code) here. Linked devices get your recent chats from this phone,
            end-to-end encrypted. Logging out here, or moving xmm to a new phone, logs them out too.
          </p>
        </>
      ) : (
        <p class="muted pad linked-note">
          This device is linked to your phone. Link or log out devices from xmm on your phone (<strong>Menu → Linked devices</strong>).
        </p>
      )}
      {error && <p class="error pad">{error}</p>}
      <ul class="device-list">
        {list?.map((d) => (
          <li key={d.id} data-id={d.id}>
            <span class="list-action-icon">
              <Icon name="devices" />
            </span>
            <div class="grow">
              <div class="chat-name">
                {d.device_name || d.platform || "Unknown device"} {d.primary ? <span class="tag">Main phone</span> : d.linked && (d.platform === "android" || d.platform === "ios") && <span class="tag">Companion</span>}{" "}
                {d.current && <span class="tag">This device</span>}
              </div>
              <div class="muted small">Last active {listTime(d.last_used_at * 1000)} · {d.ip}</div>
            </div>
            {!d.current && isPrimary() && (
              <button class="icon-btn" onClick={() => void remove(d)} aria-label={`Log out ${d.device_name}`} title="Log out">
                <Icon name="logout" size={20} />
              </button>
            )}
          </li>
        ))}
        {list === null && !error && <li class="empty">Loading…</li>}
      </ul>
    </Drawer>
  );
}

// ---- contact info (right drawer) ---------------------------------------------

export function ContactInfo({ jid }: { jid: string }) {
  const p = S.profiles.value.get(jid);
  const r = S.roster.value.get(jid);
  const saved = r && (r.subscription === "to" || r.subscription === "both");
  const name = S.displayName(jid);
  const [busy, setBusy] = useState(false);
  const [showCode, setShowCode] = useState(false);

  async function toggleContact() {
    setBusy(true);
    try {
      if (saved) {
        await api.removeContact(jid);
        toast(`${name} removed from contacts`);
      } else {
        await api.addContact(phoneOf(jid), p?.name ?? "");
        toast("Contact saved");
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <aside class="info" aria-label="Contact info">
      <header class="drawer-head">
        <button class="icon-btn" onClick={() => (infoOpen.value = false)} aria-label="Close">
          <Icon name="close" />
        </button>
        <h2>Contact info</h2>
      </header>
      <div class="drawer-body">
        <div class="info-hero">
          <Avatar url={p?.avatar_url} jid={jid} name={name} size={200} />
          <h3>{name}</h3>
          <p class="muted">{phoneOf(jid)}</p>
          {p?.username && <p class="muted info-username">@{p.username}</p>}
          {!saved && p?.name && <p class="muted">~{p.name}</p>}
        </div>
        <div class="field">
          <label>About</label>
          <div class="field-value">{p?.about ?? "—"}</div>
        </div>
        <button class="list-action encryption-row" onClick={() => setShowCode(true)}>
          <span class="list-action-icon">
            <Icon name="lock" />
          </span>
          <div>
            <div>Encryption</div>
            <small class="muted">Messages are end-to-end encrypted. Click to verify.</small>
          </div>
        </button>
        {showCode && <SecurityCode jid={jid} onClose={() => setShowCode(false)} />}
        <TimerSelect jid={jid} />
        <div class="info-actions">
          <ExportChat jid={jid} />
          <ReportButton jid={jid} />
          <button class="list-action" disabled={busy} onClick={() => void toggleContact()}>
            <span class="list-action-icon">
              <Icon name={saved ? "trash" : "personAdd"} />
            </span>
            {saved ? "Remove from contacts" : "Add to contacts"}
          </button>
          <button
            class="list-action danger"
            onClick={() => void engine.value?.setBlocked(jid, !S.blocked.value.has(jid)).then(
              () => toast(S.blocked.value.has(jid) ? `${name} blocked` : `${name} unblocked`),
              (e) => toast(e.message),
            )}
          >
            <span class="list-action-icon">
              <Icon name="block" />
            </span>
            {S.blocked.value.has(jid) ? `Unblock ${name}` : `Block ${name}`}
          </button>
          <button
            class="list-action danger"
            onClick={() => {
              infoOpen.value = false;
              void engine.value?.deleteChat(jid);
            }}
          >
            <span class="list-action-icon">
              <Icon name="trash" />
            </span>
            Delete chat on this device
          </button>
        </div>
      </div>
    </aside>
  );
}

// ---- confirm dialog ------------------------------------------------------------

export function Confirm(props: { text: string; action: string; onConfirm: () => void; onCancel: () => void }) {
  useLayoutEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && props.onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <div class="modal-backdrop" onClick={props.onCancel}>
      <div class="modal" role="alertdialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <p>{props.text}</p>
        <div class="row-buttons">
          <button class="link" onClick={props.onCancel}>
            Cancel
          </button>
          <button class="primary" autoFocus onClick={props.onConfirm}>
            {props.action}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- starred / archived ------------------------------------------------------------

export function Starred() {
  const [list, setList] = useState<Msg[] | null>(null);
  const stars = S.stars.value;
  useEffect(() => void engine.value?.starredMessages().then(setList), [stars]);
  return (
    <Drawer title="Starred messages">
      <ul class="contact-list">
        {list?.map((m) => (
          <MessageResult
            key={m.key}
            m={m}
            onOpen={() => {
              drawer.value = "none";
              void engine.value?.openChat(m.chat, m);
            }}
          />
        ))}
        {list && !list.length && <li class="empty">Star messages from their menu to find them here later.</li>}
      </ul>
    </Drawer>
  );
}

export function Archived() {
  const list = sortChats(S.chats.value.filter((c) => S.isArchived(c.jid)));
  return (
    <Drawer title="Archived">
      <ul class="chat-list">
        {list.map((c) => (
          <ChatRow key={c.jid} chat={c} />
        ))}
        {!list.length && <li class="empty">No archived chats</li>}
      </ul>
    </Drawer>
  );
}

// ---- privacy ----------------------------------------------------------------------------

const LEVELS: { v: "everyone" | "contacts" | "nobody"; label: string }[] = [
  { v: "everyone", label: "Everyone" },
  { v: "contacts", label: "My contacts" },
  { v: "nobody", label: "Nobody" },
];

export function PrivacyPanel() {
  const p = S.privacy.value;
  const [busy, setBusy] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  async function set(patch: Partial<typeof p>) {
    setBusy(true);
    try {
      await engine.value?.setPrivacy(patch);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  const select = (key: "last_seen" | "profile_photo" | "about", label: string, hint?: string) => (
    <div class="field">
      <label>{label}</label>
      <select class="level-select" value={p[key]} disabled={busy} onChange={(e) => void set({ [key]: e.currentTarget.value })} aria-label={label}>
        {LEVELS.map((l) => (
          <option key={l.v} value={l.v}>
            {l.label}
          </option>
        ))}
      </select>
      {hint && <p class="muted small">{hint}</p>}
    </div>
  );
  const blocked = [...S.blocked.value];
  return (
    <Drawer title="Privacy">
      <h3 class="section">Who can see my personal info</h3>
      {select("last_seen", "Last seen and online", "If you don't share your last seen, you won't see other people's either.")}
      {select("profile_photo", "Profile photo")}
      {select("about", "About")}
      <div class="field">
        <label class="toggle">
          <span>
            <strong>Read receipts</strong>
            <small class="muted">If turned off, you won't send or receive read receipts. Read receipts are always sent for group chats.</small>
          </span>
          <input type="checkbox" checked={p.read_receipts} disabled={busy} onChange={() => void set({ read_receipts: !p.read_receipts })} aria-label="Read receipts" />
        </label>
      </div>
      <div class="field">
        <label>Groups</label>
        <select class="level-select" value={p.groups_add} disabled={busy} onChange={(e) => void set({ groups_add: e.currentTarget.value as "everyone" | "contacts" })} aria-label="Who can add me to groups">
          <option value="everyone">Everyone</option>
          <option value="contacts">My contacts</option>
        </select>
        <p class="muted small">Who can add you to groups. Others can still send you an invite link.</p>
      </div>
      <div class="field">
        <label class="toggle">
          <span>
            <strong>Silence unknown callers</strong>
            <small class="muted">Calls from people you haven't saved won't ring. They still show up in Calls.</small>
          </span>
          <input type="checkbox" checked={!!p.silence_unknown_callers} disabled={busy} onChange={() => void set({ silence_unknown_callers: !p.silence_unknown_callers })} aria-label="Silence unknown callers" />
        </label>
      </div>
      <h3 class="section">Blocked contacts ({blocked.length})</h3>
      <ul class="contact-list blocked-list">
        {blocked.map((j) => (
          <li key={j}>
            <Avatar url={S.profiles.value.get(j)?.avatar_url} jid={j} name={S.displayName(j)} size={40} />
            <div class="grow">
              <div class="chat-name">{S.displayName(j)}</div>
              <div class="muted small">{phoneOf(j)}</div>
            </div>
            <button class="link" onClick={() => void engine.value?.setBlocked(j, false).catch((e) => toast(e.message))}>
              Unblock
            </button>
          </li>
        ))}
        {!blocked.length && <li class="empty">Blocked contacts can't call you or send you messages.</li>}
      </ul>
      <h3 class="section">Security</h3>
      <button class="list-action" onClick={() => setPinOpen(true)}>
        <span class="list-action-icon">
          <Icon name="lock" />
        </span>
        Two-step verification
      </button>
      {pinOpen && <PinDialog onClose={() => setPinOpen(false)} />}
    </Drawer>
  );
}

function PinDialog({ onClose }: { onClose: () => void }) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [current, setCurrent] = useState("");
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => void api.pinStatus().then((r) => setEnabled(r.enabled)), []);
  async function save() {
    setError("");
    if (!/^\d{6}$/.test(pin)) return setError("The PIN must be 6 digits.");
    if (pin !== confirm) return setError("The PINs don't match.");
    setBusy(true);
    try {
      await api.setPin(pin, current);
      toast("Two-step verification is on");
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  async function turnOff() {
    setBusy(true);
    try {
      await api.removePin(current);
      toast("Two-step verification is off");
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  const digits = (set: (v: string) => void) => (e: Event) => set((e.currentTarget as HTMLInputElement).value.replace(/\D/g, "").slice(0, 6));
  return (
    <div class="modal-backdrop" onClick={onClose}>
      <div class="modal pin-dialog" role="dialog" aria-label="Two-step verification" onClick={(e) => e.stopPropagation()}>
        <h2>Two-step verification</h2>
        <p>For extra security, a PIN is required when your phone number is registered on a new device.</p>
        {enabled === null ? (
          <p class="muted">Loading…</p>
        ) : (
          <>
            {enabled && <input class="line-input" type="password" inputMode="numeric" value={current} onInput={digits(setCurrent)} placeholder="Current PIN" aria-label="Current PIN" />}
            <input class="line-input" type="password" inputMode="numeric" value={pin} onInput={digits(setPin)} placeholder={enabled ? "New 6-digit PIN" : "Enter a 6-digit PIN"} aria-label="New PIN" />
            <input class="line-input" type="password" inputMode="numeric" value={confirm} onInput={digits(setConfirm)} placeholder="Confirm PIN" aria-label="Confirm PIN" />
            {error && <p class="error">{error}</p>}
            <div class="row-buttons">
              {enabled && (
                <button class="link danger" disabled={busy} onClick={() => void turnOff()}>
                  Turn off
                </button>
              )}
              <button class="link" onClick={onClose}>
                Cancel
              </button>
              <button class="primary" disabled={busy} onClick={() => void save()}>
                {enabled ? "Change PIN" : "Turn on"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Off / 24 hours / 7 days / 90 days. */
export function TimerSelect({ jid, disabled }: { jid: string; disabled?: boolean }) {
  const ttl = S.chatTTL(jid);
  return (
    <div class="field">
      <label>Disappearing messages</label>
      <select
        class="level-select"
        value={String(ttl)}
        disabled={disabled}
        aria-label="Disappearing messages"
        onChange={(e) => void engine.value?.setTimer(jid, Number(e.currentTarget.value)).catch((err) => toast(err.message ?? String(err)))}
      >
        <option value="0">Off</option>
        <option value="86400">24 hours</option>
        <option value="604800">7 days</option>
        <option value="7776000">90 days</option>
      </select>
      <p class="muted small">New messages will disappear from this chat after the selected time.</p>
    </div>
  );
}

// ---- security code --------------------------------------------------------------------

export function SecurityCode({ jid, onClose }: { jid: string; onClose: () => void }) {
  const [code, setCode] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    void engine.value?.securityCode(jid).then(setCode, () => setCode(null));
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [jid]);
  const groups = code ? code.match(/.{1,5}/g) ?? [] : [];
  return (
    <div class="modal-backdrop" onClick={onClose}>
      <div class="modal security-code" role="dialog" aria-label="Security code" onClick={(e) => e.stopPropagation()}>
        <h2>Verify security code</h2>
        {code === undefined ? (
          <p class="muted">Computing…</p>
        ) : code === null ? (
          <p class="muted">{S.displayName(jid)} hasn't set up encryption on any device yet.</p>
        ) : (
          <>
            <div class="code-grid" aria-label="Security code">
              {groups.map((g, i) => (
                <span key={i}>{g}</span>
              ))}
            </div>
            <p class="muted small">
              Compare these numbers with {S.displayName(jid)}, in person or on a call you trust. If they match, nobody — not even the
              server — can read your messages. The code changes when either of you links or removes a device.
            </p>
          </>
        )}
        <div class="row-buttons">
          <button class="primary" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
